import Log from '../../common/log';
import { SessionsTag } from '../../common/Constants';
/**
 * Assembles parameters for different session event types
 */
export default class ParamsAssembler {
    constructor(registeredPropertiesGetter) {
        this.registeredPropertiesGetter = registeredPropertiesGetter;
    }
    /**
     * Assemble parameters for session start
     * @param dimensions - Event dimensions
     * @param options - Additional options (hasEndDatetime, etc.)
     */
    assembleForStart(dimensions = {}, options) {
        const params = this.buildBaseParams(dimensions, null, null, true, true, options === null || options === void 0 ? void 0 : options.hasEndDatetime);
        return this.parseInternalParams(params);
    }
    /**
     * Assemble parameters for session stop
     * @param params - Stop parameters
     */
    assembleForStop(params = {}) {
        return this.parseInternalParams(params);
    }
    /**
     * Assemble parameters for event
     * @param eventName - Event name
     * @param dimensions - Event dimensions
     * @param values - Event values
     * @param topLevelDimensions - Top-level dimensions to merge
     * @param options - Additional options (hasEndDatetime, etc.)
     */
    assembleForEvent(eventName = '', dimensions = {}, values = {}, topLevelDimensions = {}, options) {
        const builtParams = this.buildBaseParams(dimensions, values, eventName, false, false, options === null || options === void 0 ? void 0 : options.hasEndDatetime);
        // Merge top-level dimensions
        Object.assign(builtParams.params, topLevelDimensions || {});
        return this.parseInternalParams(builtParams);
    }
    /**
     * Assemble parameters for event end
     * @param eventName - Event name
     * @param topLevelDimensions - Top-level dimensions to merge
     */
    assembleForEventEnd(eventName = '', topLevelDimensions = {}) {
        const builtParams = this.buildBaseParams(null, null, eventName, false, false, false);
        // Merge top-level dimensions
        Object.assign(builtParams.params, topLevelDimensions || {});
        return this.parseInternalParams(builtParams);
    }
    /**
     * Assemble parameters for navigation
     * @param dimensions - Navigation dimensions (should include page/route)
     * @param isStart - Whether this is a start navigation
     */
    assembleForNav(dimensions = {}, isStart = false) {
        const params = this.buildBaseParams(dimensions, null, null, true, isStart, undefined);
        return this.parseInternalParams(params);
    }
    /**
     * Add error fields to base parameters
     * @param baseParams - Base parameters object
     * @param errorFields - Error fields (code, msg, type, metadata, duration)
     */
    assembleForError(baseParams, errorFields) {
        baseParams.errorCode = errorFields.code;
        baseParams.errorMsg = errorFields.msg;
        baseParams.errorType = errorFields.errorType || '';
        baseParams.errorMetadata = errorFields.metadata || '';
        baseParams.duration = errorFields.duration || 0;
        return baseParams;
    }
    /**
     * Build base params structure with registered properties merge
     */
    buildBaseParams(dimensions, values, eventName, isNavigation, isStart, hasEndDatetime) {
        const returnparams = {};
        if (eventName)
            returnparams.name = eventName;
        returnparams.dimensions = dimensions || {};
        returnparams.values = values || {};
        // Merge registered properties
        const registeredProperties = this.registeredPropertiesGetter();
        if (registeredProperties) {
            for (const key in registeredProperties.dimensions) {
                returnparams.dimensions[key] = registeredProperties.dimensions[key];
            }
            for (const key2 in registeredProperties.values) {
                returnparams.values[key2] = registeredProperties.values[key2];
            }
        }
        const paramsObject = { params: returnparams };
        // Navigation-specific transformations
        if (isNavigation) {
            if (paramsObject.params.dimensions.page) {
                paramsObject.params.page = paramsObject.params.dimensions.page;
                delete paramsObject.params.dimensions.page;
            }
            if (paramsObject.params.dimensions.route) {
                paramsObject.params.route = paramsObject.params.dimensions.route;
                delete paramsObject.params.dimensions.route;
            }
            if (!isStart) {
                delete paramsObject.params.dimensions;
            }
            delete paramsObject.params.values;
        }
        // Add hasEndDatetime flag if specified
        if (hasEndDatetime) {
            paramsObject.params.hasEndDatetime = hasEndDatetime;
        }
        // Remove empty dimensions and values objects
        try {
            if (paramsObject.params) {
                if (paramsObject.params.dimensions &&
                    typeof paramsObject.params.dimensions === 'object' &&
                    Object.keys(paramsObject.params.dimensions).length === 0) {
                    delete paramsObject.params.dimensions;
                }
                if (paramsObject.params.values &&
                    typeof paramsObject.params.values === 'object' &&
                    Object.keys(paramsObject.params.values).length === 0) {
                    delete paramsObject.params.values;
                }
            }
        }
        catch (e) {
            Log.error(SessionsTag, 'Error removing empty dimensions and values from params json', e);
        }
        return paramsObject;
    }
    /**
     * Parse internal params object (unwrap params.params)
     */
    parseInternalParams(params) {
        if (params) {
            return params.params || params || {};
        }
        else {
            return {};
        }
    }
}
